/**
 * Tactical-board formation presets.
 *
 * Faithful port of `FORMATION_PRESETS` from the legacy
 * `js/tactical-board.js` (~line 139). Each preset is pure data — no
 * functions, no side effects. Position coordinates are normalized
 * 0..1 across pitch length (x) and width (y), assuming a landscape
 * orientation with the home team attacking left-to-right (keeper at
 * x≈0.07, strikers at x≈0.75).
 *
 * The label is the legacy jersey-number convention ("GK", "2", "9",
 * etc.). It's display-only; future phases may bind player_id to
 * anchors but the preset doesn't carry that — applying a formation
 * always writes `player_id: ""` per legacy behavior.
 *
 * To add a new formation: extend the FORMATION_PRESETS map. To add a
 * game format, also extend `GAME_FORMATS`. The "custom" 11v11
 * sentinel (positions: null) is preserved — applying it tags the
 * board with `formation: "custom"` without synthesizing positions, so
 * a hand-built lineup can carry the metadata.
 */

import { clamp01, type Point } from "./coords";

export const GAME_FORMATS = ["7v7", "9v9", "11v11"] as const;
export type GameFormat = (typeof GAME_FORMATS)[number];

export type FormationPosition = {
  /** Normalized x coordinate 0..1, attacking left-to-right. */
  x: number;
  /** Normalized y coordinate 0..1, top-to-bottom on the pitch. */
  y: number;
  /** Jersey number / role label (GK, 2, 9, etc.). Display-only. */
  label: string;
};

export type FormationPreset = {
  /** Stable id used in `tactical_board_json.formation`. */
  id: string;
  /** Human-readable name shown in the picker. */
  label: string;
  /** Position list. `null` ONLY for the 11v11 "custom" sentinel —
   * applying that preset tags the board with formation: "custom" and
   * preserves existing player tokens unchanged. */
  positions: FormationPosition[] | null;
};

/** All presets, indexed by game format. The order within each format
 * controls picker order in the UI.
 *
 * **Verbatim port of legacy `js/tactical-board.js` `FORMATION_PRESETS`
 * (~line 147).** Every coordinate and jersey label below matches the
 * legacy registry exactly — an earlier M3 pass re-tuned several
 * positions + labels, which the M1–M3 parity audit flagged as drift.
 * `formations.test.ts` carries a coordinate-snapshot test so any
 * future change to these values is caught in review. If you need to
 * adjust a preset, update legacy AND here AND the snapshot together. */
export const FORMATION_PRESETS: Record<GameFormat, FormationPreset[]> = {
  "7v7": [
    {
      id: "2-3-1",
      label: "2-3-1",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.25, y: 0.32, label: "2" },
        { x: 0.25, y: 0.68, label: "3" },
        { x: 0.5, y: 0.25, label: "7" },
        { x: 0.5, y: 0.5, label: "6" },
        { x: 0.5, y: 0.75, label: "11" },
        { x: 0.75, y: 0.5, label: "9" },
      ],
    },
    {
      id: "3-2-1",
      label: "3-2-1",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.25, y: 0.25, label: "2" },
        { x: 0.25, y: 0.5, label: "4" },
        { x: 0.25, y: 0.75, label: "3" },
        { x: 0.5, y: 0.35, label: "8" },
        { x: 0.5, y: 0.65, label: "6" },
        { x: 0.75, y: 0.5, label: "9" },
      ],
    },
    {
      id: "2-1-2-1",
      label: "2-1-2-1",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.22, y: 0.35, label: "2" },
        { x: 0.22, y: 0.65, label: "3" },
        { x: 0.4, y: 0.5, label: "6" },
        { x: 0.6, y: 0.3, label: "7" },
        { x: 0.6, y: 0.7, label: "11" },
        { x: 0.78, y: 0.5, label: "9" },
      ],
    },
  ],
  "9v9": [
    {
      id: "3-2-3",
      label: "3-2-3",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.22, y: 0.25, label: "2" },
        { x: 0.22, y: 0.5, label: "4" },
        { x: 0.22, y: 0.75, label: "3" },
        { x: 0.45, y: 0.35, label: "8" },
        { x: 0.45, y: 0.65, label: "6" },
        { x: 0.72, y: 0.22, label: "7" },
        { x: 0.72, y: 0.5, label: "9" },
        { x: 0.72, y: 0.78, label: "11" },
      ],
    },
    {
      id: "3-3-2",
      label: "3-3-2",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.22, y: 0.25, label: "2" },
        { x: 0.22, y: 0.5, label: "4" },
        { x: 0.22, y: 0.75, label: "3" },
        { x: 0.48, y: 0.25, label: "7" },
        { x: 0.48, y: 0.5, label: "8" },
        { x: 0.48, y: 0.75, label: "11" },
        { x: 0.74, y: 0.4, label: "9" },
        { x: 0.74, y: 0.6, label: "10" },
      ],
    },
    {
      id: "2-3-3",
      label: "2-3-3",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.22, y: 0.35, label: "2" },
        { x: 0.22, y: 0.65, label: "3" },
        { x: 0.45, y: 0.25, label: "6" },
        { x: 0.45, y: 0.5, label: "8" },
        { x: 0.45, y: 0.75, label: "10" },
        { x: 0.72, y: 0.22, label: "7" },
        { x: 0.72, y: 0.5, label: "9" },
        { x: 0.72, y: 0.78, label: "11" },
      ],
    },
    {
      id: "4-3-1",
      label: "4-3-1",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.22, y: 0.2, label: "2" },
        { x: 0.22, y: 0.4, label: "4" },
        { x: 0.22, y: 0.6, label: "5" },
        { x: 0.22, y: 0.8, label: "3" },
        { x: 0.5, y: 0.3, label: "7" },
        { x: 0.5, y: 0.5, label: "8" },
        { x: 0.5, y: 0.7, label: "11" },
        { x: 0.75, y: 0.5, label: "9" },
      ],
    },
  ],
  "11v11": [
    {
      id: "4-3-3",
      label: "4-3-3",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.2, y: 0.18, label: "2" },
        { x: 0.2, y: 0.38, label: "4" },
        { x: 0.2, y: 0.62, label: "5" },
        { x: 0.2, y: 0.82, label: "3" },
        { x: 0.45, y: 0.3, label: "8" },
        { x: 0.45, y: 0.5, label: "6" },
        { x: 0.45, y: 0.7, label: "10" },
        { x: 0.72, y: 0.2, label: "7" },
        { x: 0.72, y: 0.5, label: "9" },
        { x: 0.72, y: 0.8, label: "11" },
      ],
    },
    {
      id: "4-2-3-1",
      label: "4-2-3-1",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.2, y: 0.18, label: "2" },
        { x: 0.2, y: 0.38, label: "4" },
        { x: 0.2, y: 0.62, label: "5" },
        { x: 0.2, y: 0.82, label: "3" },
        { x: 0.4, y: 0.4, label: "6" },
        { x: 0.4, y: 0.6, label: "8" },
        { x: 0.6, y: 0.22, label: "7" },
        { x: 0.6, y: 0.5, label: "10" },
        { x: 0.6, y: 0.78, label: "11" },
        { x: 0.78, y: 0.5, label: "9" },
      ],
    },
    {
      id: "4-4-2",
      label: "4-4-2",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        { x: 0.2, y: 0.18, label: "2" },
        { x: 0.2, y: 0.38, label: "4" },
        { x: 0.2, y: 0.62, label: "5" },
        { x: 0.2, y: 0.82, label: "3" },
        { x: 0.45, y: 0.18, label: "7" },
        { x: 0.45, y: 0.38, label: "6" },
        { x: 0.45, y: 0.62, label: "8" },
        { x: 0.45, y: 0.82, label: "11" },
        { x: 0.72, y: 0.4, label: "9" },
        { x: 0.72, y: 0.6, label: "10" },
      ],
    },
    {
      id: "3-5-2",
      label: "3-5-2",
      positions: [
        { x: 0.07, y: 0.5, label: "GK" },
        // Back three: 4 / 5 / 6 (centre-backs).
        { x: 0.2, y: 0.3, label: "4" },
        { x: 0.2, y: 0.5, label: "5" },
        { x: 0.2, y: 0.7, label: "6" },
        // Midfield five: 2 (right-wing-back), 8 / 10 (centre-mids),
        // 7 (right-side mid), 3 (left-wing-back).
        { x: 0.42, y: 0.12, label: "2" },
        { x: 0.42, y: 0.35, label: "8" },
        { x: 0.42, y: 0.5, label: "10" },
        { x: 0.42, y: 0.65, label: "7" },
        { x: 0.42, y: 0.88, label: "3" },
        // Front two.
        { x: 0.72, y: 0.4, label: "9" },
        { x: 0.72, y: 0.6, label: "11" },
      ],
    },
    {
      id: "custom",
      label: "Custom (keep current tokens)",
      positions: null,
    },
  ],
};

export function isGameFormat(value: unknown): value is GameFormat {
  return (
    typeof value === "string" && (GAME_FORMATS as readonly string[]).includes(value)
  );
}

/** Look up the preset list for a game format. Returns an empty array
 * for an unknown format rather than throwing — the picker UI can
 * gracefully degrade. */
export function formationsForGameFormat(gameFormat: GameFormat): FormationPreset[] {
  return FORMATION_PRESETS[gameFormat] ?? [];
}

/** Resolve formation positions for a given format + id. Returns:
 *   - the positions array when the preset is regular
 *   - `null` for the 11v11 "custom" sentinel (caller preserves
 *     existing tokens)
 *   - `undefined` when the format/id pair doesn't exist
 */
export function formationPositions(
  gameFormat: GameFormat,
  formationId: string,
): FormationPosition[] | null | undefined {
  const preset = formationsForGameFormat(gameFormat).find(
    (f) => f.id === formationId,
  );
  if (!preset) return undefined;
  return preset.positions;
}

/** Materialize a formation's positions into player-token shape ready
 * for the tactical_board scene. Caller still owns id generation,
 * conflict resolution with existing tokens, and the 40-token cap. */
export function playerTokensFromFormation(
  gameFormat: GameFormat,
  formationId: string,
  idGenerator: () => string,
): Array<{
  kind: "player";
  id: string;
  x: number;
  y: number;
  label: string;
  player_id: string;
}> | null {
  const positions = formationPositions(gameFormat, formationId);
  if (positions === null || positions === undefined) return null;
  return positions.map((p) => ({
    kind: "player" as const,
    id: idGenerator(),
    x: clamp01(p.x),
    y: clamp01(p.y),
    label: p.label,
    player_id: "",
  }));
}

/** Type guard for a Point with positive finite coords (used in tests
 * + when normalizing raw input). */
export function isPoint(value: unknown): value is Point {
  return (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    "y" in value &&
    typeof (value as Point).x === "number" &&
    typeof (value as Point).y === "number"
  );
}
